import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";

import { projectFieldHelp } from "../../app/guidance/fieldHelpRegistry";
import { guided } from "../../app/guidance/fieldHelpRegistryCore";
import { Card, Page } from "../../app/layout/Page";
import {
  projectApi,
  selectedProjectId,
  selectProject,
} from "../../app/workflow";

import type { VerticalDatum, VerticalReference } from "../network";

import "./projects.css";

type ProjectTerrainPath =
  "copernicus_egm2008" | "surveyed_manual" | "usgs_navd88";

const SURVEYED_DATUM_OPTIONS: Array<{
  label: string;
  value: VerticalDatum;
}> = [
  { label: "NAVD88", value: "NAVD88" },
  { label: "NGVD29", value: "NGVD29" },
  { label: "EGM2008 · EPSG:3855", value: "EGM2008" },
  { label: "WGS84 ellipsoidal height", value: "WGS84_ELLIPSOID" },
  { label: "Local project datum", value: "LOCAL_PROJECT_DATUM" },
  { label: "Other declared datum", value: "OTHER" },
];

function projectVerticalReference(
  path: ProjectTerrainPath,
  manualDatum: VerticalDatum,
  customIdentifier: string,
): VerticalReference {
  if (path === "usgs_navd88") return { datum: "NAVD88" };
  if (path === "copernicus_egm2008") return { datum: "EGM2008" };
  return manualDatum === "LOCAL_PROJECT_DATUM" || manualDatum === "OTHER"
    ? { custom_identifier: customIdentifier.trim(), datum: manualDatum }
    : { datum: manualDatum };
}

function datumSummary(
  path: ProjectTerrainPath,
  manualDatum: VerticalDatum,
): string {
  if (path === "usgs_navd88") return "Project datum: NAVD88.";
  if (path === "copernicus_egm2008") return "Project datum: EGM2008.";
  return `Project datum: ${manualDatum}.`;
}

export function ProjectsPage() {
  const queryClient = useQueryClient();
  const projects = useQuery({
    queryFn: projectApi.list,
    queryKey: ["flow-wizard", "projects"],
  });
  const selected =
    projects.data?.find((project) => project.id === selectedProjectId()) ??
    null;
  const [name, setName] = useState("");
  const [terrainPath, setTerrainPath] =
    useState<ProjectTerrainPath>("usgs_navd88");
  const [manualDatum, setManualDatum] = useState<VerticalDatum>("NGVD29");
  const [customDatumIdentifier, setCustomDatumIdentifier] = useState("");
  const [notice, setNotice] = useState<string | null>(null);
  const manualNeedsIdentifier =
    manualDatum === "LOCAL_PROJECT_DATUM" || manualDatum === "OTHER";
  const verticalReference = projectVerticalReference(
    terrainPath,
    manualDatum,
    customDatumIdentifier,
  );
  const create = useMutation({
    mutationFn: () => projectApi.create(name, verticalReference),
    onSuccess: async (project) => {
      selectProject(project.id);
      setName("");
      setTerrainPath("usgs_navd88");
      setManualDatum("NGVD29");
      setCustomDatumIdentifier("");
      setNotice(`Created and selected ${project.name}.`);
      await queryClient.invalidateQueries({
        queryKey: ["flow-wizard", "projects"],
      });
    },
  });
  const hasSavedProjects = (projects.data?.length ?? 0) > 0;

  if (projects.isPending) {
    return (
      <Page title="Projects">
        <Card>
          <p role="status">Loading projects…</p>
        </Card>
      </Page>
    );
  }

  if (projects.isError) {
    return (
      <Page title="Projects">
        <Card title="Projects unavailable">
          <p role="alert">{projects.error.message}</p>
          <button type="button" onClick={() => void projects.refetch()}>
            Try again
          </button>
        </Card>
      </Page>
    );
  }

  return (
    <Page title="Projects">
      <div className="projects-stack">
        {notice === null ? null : <p role="status">{notice}</p>}
        {!hasSavedProjects ? null : (
          <Card title="Saved projects">
            <ul className="project-list">
                {projects.data.map((project) => (
                  <li
                    className={selected?.id === project.id ? "is-selected" : ""}
                    key={project.id}
                  >
                    <div>
                      <strong>{project.name}</strong>
                      <span>
                        {project.read_only
                          ? "Portable import · read-only"
                          : "Editable"}
                      </span>
                      {project.saved_simulation_count > 0 ||
                      project.saved_optimization_count > 0 ? (
                        <small>
                          {project.saved_simulation_count}{" "}
                          {project.saved_simulation_count === 1
                            ? "simulation"
                            : "simulations"}{" "}
                          · {project.saved_optimization_count}{" "}
                          {project.saved_optimization_count === 1
                            ? "optimization"
                            : "optimizations"}
                        </small>
                      ) : null}
                      <details className="projects-details">
                        <summary
                          aria-label={`Project details for ${project.name}`}
                        >
                          Project details
                        </summary>
                        <small>Revision {project.revision}</small>
                      </details>
                    </div>
                    <button
                      aria-label={`${
                        selected?.id === project.id ? "Selected" : "Select"
                      } ${project.name}`}
                      aria-pressed={selected?.id === project.id}
                      type="button"
                      onClick={() => {
                        selectProject(project.id);
                        setNotice(`Selected ${project.name}.`);
                        void queryClient.invalidateQueries();
                      }}
                    >
                      {selected?.id === project.id ? "Selected" : "Select"}
                    </button>
                  </li>
                ))}
            </ul>
          </Card>
        )}

        {selected === null ? null : (
          <aside className="projects-next" aria-label="Selected project">
            <span>{selected.name}</span>
            <Link
              className="button button--primary"
              to={selected.read_only ? "/results" : "/network"}
            >
              {selected.read_only ? "Open results" : "Build route"}
            </Link>
          </aside>
        )}

        <details
          className="projects-new"
          open={hasSavedProjects ? undefined : true}
        >
          <summary>New project</summary>
          <Card className="projects-create" title="Start a project">
          <div className="projects-form">
            <label>
              Project name
              <input
                {...guided(projectFieldHelp.name)}
                value={name}
                onChange={(event) => setName(event.currentTarget.value)}
              />
            </label>
            <label>
              Terrain source
              <select
                {...guided(projectFieldHelp.terrainPath)}
                aria-describedby="project-datum-guidance"
                onChange={(event) =>
                  setTerrainPath(
                    event.currentTarget.value as ProjectTerrainPath,
                  )
                }
                value={terrainPath}
              >
                <option value="usgs_navd88">USGS · U.S. · NAVD88</option>
                <option value="copernicus_egm2008">
                  Copernicus · global · EGM2008
                </option>
                <option value="surveyed_manual">Surveyed or manual</option>
              </select>
            </label>
            {terrainPath === "surveyed_manual" ? (
              <>
                <label>
                  Vertical datum
                  <select
                    {...guided(projectFieldHelp.manualDatum)}
                    aria-describedby="project-datum-guidance"
                    onChange={(event) =>
                      setManualDatum(event.currentTarget.value as VerticalDatum)
                    }
                    value={manualDatum}
                  >
                    {SURVEYED_DATUM_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                {manualNeedsIdentifier ? (
                  <label>
                    Datum identifier
                    <input
                      {...guided(projectFieldHelp.customDatum)}
                      aria-describedby="project-custom-datum-guidance"
                      required
                      value={customDatumIdentifier}
                      onChange={(event) =>
                        setCustomDatumIdentifier(event.currentTarget.value)
                      }
                    />
                  </label>
                ) : null}
              </>
            ) : null}
          </div>
          <p id="project-datum-guidance" role="note">
            {datumSummary(terrainPath, manualDatum)}
          </p>
          {manualNeedsIdentifier && terrainPath === "surveyed_manual" ? (
            <p id="project-custom-datum-guidance" role="note">
              Use the exact benchmark or datum name.
            </p>
          ) : null}
          <details className="projects-details">
            <summary>Datum rules</summary>
            <p>
              Elevations must use the project datum. Datums are never mixed or
              relabeled. Google Elevation is not used.
            </p>
          </details>
          <button
            disabled={
              create.isPending ||
              name.trim() === "" ||
              (terrainPath === "surveyed_manual" &&
                manualNeedsIdentifier &&
                customDatumIdentifier.trim() === "")
            }
            type="button"
            onClick={() => create.mutate()}
          >
            Create project
          </button>
          {create.error === null ? null : (
            <p role="alert">{create.error.message}</p>
          )}
          </Card>
        </details>
      </div>
    </Page>
  );
}
